import { Ring, sparklinePoints } from './charts'
import type { HealthRatio } from '../../../data/ServerApiClient'
import { deltaColor, formatDelta, healthToneColors } from './cfoHealthLabels'

// One scored ratio inside an expanded category: a score ring + grade + this-month move + its share of
// the category score + a sub-score trend line. The sub-score, weight, and series are computed
// server-side (ratio_health.rs) and served on the category, so this stays a pure presentation card.

export function RatioScoreCard({
  ratio,
  showGrades = true,
  onSelect,
}: {
  ratio: HealthRatio
  showGrades?: boolean
  onSelect?: (ratioId: string) => void
}) {
  const tone = healthToneColors(ratio.score)
  const weightPct = Math.round((ratio.weight ?? 0) * 100)
  const seriesValues = (ratio.series ?? []).map((p) => p.score)
  const sparkColor =
    seriesValues.length >= 2 && seriesValues[seriesValues.length - 1] < seriesValues[0]
      ? 'var(--neg, #c0492f)'
      : 'var(--pos, #0e7c86)'
  const clickable = !!onSelect

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onSelect(ratio.id) : undefined}
      onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onSelect(ratio.id) : undefined}
      title={clickable ? `Open ${ratio.label} detail` : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
        padding: '10px 12px',
        background: 'var(--surface)',
        border: '1px solid var(--hair)',
        borderRadius: 'var(--r1, 10px)',
        cursor: clickable ? 'pointer' : 'default',
        textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Ring
          value={ratio.score / 100}
          label={Math.round(ratio.score)}
          size={40}
          stroke={5}
          color={tone.color}
          ariaLabel={`${ratio.label} health ${Math.round(ratio.score)} of 100`}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--ink)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={ratio.label}
          >
            {ratio.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            {showGrades ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: tone.color,
                  background: tone.bg,
                  padding: '1px 7px',
                  borderRadius: 999,
                }}
              >
                {ratio.grade}
              </span>
            ) : null}
            {ratio.delta_1mo != null && Math.abs(ratio.delta_1mo) >= 0.5 ? (
              <span style={{ fontSize: 12, fontWeight: 600, color: deltaColor(ratio.delta_1mo) }}>
                {formatDelta(ratio.delta_1mo)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {weightPct}% of category
        </span>
        {seriesValues.length >= 2 ? (
          <svg width={72} height={20} viewBox="0 0 72 20" preserveAspectRatio="none" role="img" aria-label={`${ratio.label} trend`}>
            <polyline
              points={sparklinePoints(seriesValues, 72, 20)}
              fill="none"
              stroke={sparkColor}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </div>
    </div>
  )
}

export default RatioScoreCard
